import { Router } from "@angular/router";
import { Driver, DriveStep } from "driver.js";
import { FeatureTour } from "src/app/common/services/feature-tour.service";
import { waitForElement } from "src/app/common/tools/domUtils";

/** Tour for the new features introduced in version 1.59.0 */
export const newFeatures: (router?: Router) => FeatureTour = (router?: Router) => ({
  id: "new-features-1_59_0",
  title: $localize`:@@tour_new_features_1_59_0_title:Nouvelles fonctionnalités de l'application`,
  description: $localize`:@@tour_new_features_1_59_0_description:Il est désormais possible de sélectionner et télécharger des phrases spécifiques à partir de toutes les transcriptions audio et vidéo.
De plus, plusieurs utilisateurs peuvent désormais gérer ensemble l’espace documents.`,
  version: "1.0.0",
  startPage: "/transcriptions?page=0",
  priority: 1,
  disableBackButton: true,
  steps: [
    /** Sentence selection and download feature */
    {
      element: ".p-datatable-wrapper",
      advanceOn: {
        event: "click",
        selector: "body",
      },
      popover: {
        title: $localize`:@@tour_new_features_1_59_0_step1_title:Sélectionnez un audio ou une vidéo`,
        description: $localize`:@@tour_new_features_1_59_0_step1_description:Depuis n’importe quelle page qui gère les transcriptions (audio et vidéo, Sony Hive, PlayRTS, etc.), sélectionnez un audio ou une vidéo pour accéder au détail.`,
        align: "start",
        onNextClick: (element: Element | undefined, step: DriveStep, options: { driver: Driver }): void => {
          if (element && element instanceof HTMLElement) {
            const row = element.querySelector(".clickable-row");
            if (row && row instanceof HTMLElement) {
              row.click();
            }
          }
          waitForElement("app-audio-transcription button:nth-of-type(2)", 2000)
            .then(() => {
              options.driver.moveNext();
            })
            .catch(() => {
              console.error("Element not found: app-audio-transcription button:nth-of-type(2)");
            });
        },
      },
    },
    {
      element: "app-audio-transcription button:nth-of-type(2)",
      advanceOn: {
        event: "click",
        selector: "body",
      },
      popover: {
        title: $localize`:@@tour_new_features_1_59_0_step2_title:Activez le mode de sélection`,
        description: $localize`:@@tour_new_features_1_59_0_step2_description:Cliquez sur le bouton pour activer le mode qui permet de sélectionner les phrases de la transcription.`,
        onNextClick: (element: Element | undefined, step: DriveStep, options: { driver: Driver }): void => {
          const downloadButton = document.querySelector("app-audio-transcription button:nth-of-type(3)");
          if (element && element instanceof HTMLElement && !downloadButton) {
            element.click();
          }
          options.driver.moveNext();
        },
      },
    },
    {
      element: "app-speech-segment span",
      popover: {
        title: $localize`:@@tour_new_features_1_59_0_step3_title:Sélectionnez les phrases`,
        description: $localize`:@@tour_new_features_1_59_0_step3_description:Sélectionnez les phrases qui vous intéressent en les survolant avec la souris. Les phrases sont automatiquement sélectionnées dans leur intégralité. Pour les désélectionner, survolez à nouveau les phrases que vous souhaitez retirer.`,
        side: "bottom",
      },
    },
    {
      element: "app-audio-transcription button:nth-of-type(3)",
      popover: {
        title: $localize`:@@tour_new_features_1_59_0_step4_title:Téléchargez les phrases sélectionnées`,
        description: $localize`:@@tour_new_features_1_59_0_step4_description:Une fois les phrases sélectionnées, cliquez sur le bouton pour télécharger un fichier ZIP contenant les phrases choisies. Vous pouvez également cliquer n'importe où sur la page pour continuer.`,
        side: "bottom",
        onNextClick: (element: Element | undefined, step: DriveStep, options: { driver: Driver }): void => {
          if (router) {
            void router
              .navigate(["/documents-spaces"])
              .then(async () => void (await waitForElement("app-documents-space-list button", 5000)))
              .then(() => {
                setTimeout(() => {
                  void options.driver.moveNext();
                }, 100);
              })
              .catch(console.error);
          } else {
            console.warn("Router is not provided, cannot navigate to /document-spaces");
          }
        },
      },
    },
    /** Document management feature */
    {
      element: "app-documents-space-list button",
      advanceOn: "click",
      popover: {
        title: $localize`:@@tour_new_features_1_59_0_step5_title:Selectionnez plusieurs administrateurs d'un espace`,
        description: $localize`:@@tour_new_features_1_59_0_step5_description:Depuis la page de gestion des espaces documents, vous pouvez désormais sélectionner plusieurs administrateurs pour un espace. Cliquez sur le bouton pour creer un nouvel espace.`,
        onNextClick: (element: Element | undefined, step: DriveStep, options: { driver: Driver }): void => {
          const dialog = document.querySelector("p-dynamicdialog");
          if (element && element instanceof HTMLElement && !dialog) {
            element.click();
          }
          waitForElement("app-documents-space-settings p-multiselect div", 2000)
            .then(() => {
              setTimeout(() => {
                options.driver.moveNext();
              }, 200);
            })
            .catch(() => {
              console.error("Element not found: app-documents-space-settings p-multiselect div");
            });
        },
      },
    },
    {
      element: "p-dynamicdialog [role='dialog']",
      disableActiveInteraction: true,
      popover: {
        title: $localize`:@@tour_new_features_1_59_0_step6_title:Ajoutez des administrateurs`,
        description: $localize`:@@tour_new_features_1_59_0_step6_description:Pendant la création ou la modification d'un espace, vous pouvez désormais ajouter plusieurs administrateurs. Seulement le propriétaire de l'espace peut ajouter ou supprimer des administrateurs. Terminer la guide et essayer cette fonctionnalité en ajoutant un administrateur.`,
      },
    },
  ],
});
